import {
  createContext, useContext, useState, type ReactNode,
} from "react";
import { OnboardingModal } from "@/features/card/components/OnboardingModal";
import { useOnboardingModal } from "@/shared/hooks/useOnboardingModal";

type OnboardingContextType = {
  isModalOpen: boolean
  openOnboardingModal: (cardId?: string) => void
  closeOnboardingModal: () => void
  hasUnactivatedCard: boolean
  activatingCardId: string | null
  startCardActivation: (cardId: string) => void
  completeCardActivation: () => void
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

type OnboardingProviderProps = { children: ReactNode };

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const {
    isModalOpen,
    openModal,
    closeModal,
    hasUnactivatedCard,
    unactivatedCardId,
  } = useOnboardingModal();

  const [activatingCardId, setActivatingCardId] = useState<string | null>(null);

  const openOnboardingModal = (cardId?: string) => {
    openModal(cardId);
  };

  const closeOnboardingModal = () => {
    closeModal();
  };

  const startCardActivation = (cardId: string) => {
    setActivatingCardId(cardId);
  };

  const completeCardActivation = () => {
    setActivatingCardId(null);
  };

  const value: OnboardingContextType = {
    isModalOpen,
    openOnboardingModal,
    closeOnboardingModal,
    hasUnactivatedCard,
    activatingCardId,
    startCardActivation,
    completeCardActivation,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      {isModalOpen && !!unactivatedCardId && (
        <OnboardingModal
          isOpen={isModalOpen}
          onClose={closeOnboardingModal}
          cardId={unactivatedCardId || undefined}
        />
      )}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboardingContext must be used within an OnboardingProvider");
  }
  return context;
};
